import React, {
  forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'next-i18next';
import { useMediaQuery } from 'react-responsive';
import { useAppContext } from '../../../src/store/AppContext';
import lockBodyScroll from '../../../src/utils/lockBodyScroll';

// Icons
import CrossIcon from '../../../public/icons/cross.svg';
import Arrow from '../../../public/icons/arrow.svg';

// Components
import ImagesSlider from '../ImagesSlider/ImagesSlider';

import styles from './LightboxImages.module.scss';

const LightboxImages = forwardRef(({ images }, ref) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [, stateDispatch] = useAppContext();

  const sliderRef = useRef();

  const [isOpened, setIsOpened] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(1);

  useImperativeHandle(ref, () => ({
    open() {
      sliderRef.current.scrollTo(0, true);
      setIsOpened(true);
    },
  }));

  useEffect(() => {
    stateDispatch({ type: 'SET_IS_HEADER_FOLDED', payload: isOpened && isMobile });
    lockBodyScroll(isOpened);
  }, [isOpened, isMobile]);

  return (
    <section className={`${styles.lightbox} ${isOpened ? styles.is_opened : ''}`}>
      <button
        type="button"
        className={styles.backdrop}
        onClick={() => setIsOpened(false)}
        aria-label="close"
      />
      <div className={styles.images}>
        <div className={styles.counter}>
          {`${currentIndex + 1} / ${images.length}`}
        </div>

        <ImagesSlider
          ref={sliderRef}
          className={styles.slider}
          images={images}
          setCurrentIndex={setCurrentIndex}
        />

        <button
          type="button"
          className={`${styles.close_btn} flex a-center fs-14 c-pointer`}
          onClick={() => setIsOpened(false)}
        >
          {t('camp.details.lightbox.close')}
          <CrossIcon className="m-l-10 p-t-2" width={16} height={16} />
        </button>

        <button
          type="button"
          className={`${styles.nav} ${styles.prev}`}
          onClick={() => sliderRef.current.prev()}
        >
          <Arrow />
        </button>
        <button
          type="button"
          className={`${styles.nav} ${styles.next}`}
          onClick={() => sliderRef.current.next()}
        >
          <Arrow />
        </button>
      </div>
    </section>
  );
});

LightboxImages.displayName = 'LightboxImages';

LightboxImages.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default LightboxImages;
